import type { ElementState } from '@/common/composables/usePageData'
import { resolveChildEditableFromPanelState } from '@/common/utils/panelEditableCascade'
import {
  coerceStateBoolean,
  findElementPageState,
} from '@/common/utils/resolveActionPageState'
import { actionIdsEqual } from '@/common/utils/resolveActionRegistryEntry'
import { isTilesRowScopedRegistryName } from '@/common/utils/tilesSetInlineVisibility'

export type ReplayVisibilityState = {
  is_visible?: unknown
  is_editable?: unknown
  element_level?: unknown
  element_id?: unknown
  element_name?: unknown
}

export type ReplayStoredPageVisibilityDeps = {
  setVisibleByLevel: (
    level: string,
    id: string | number,
    visible: boolean,
    options?: { propagateChildren?: boolean },
  ) => void
  setVisibleByName?: (name: string, visible: boolean) => void
  setEditableByLevel?: (level: string, id: string | number, editable: boolean) => void
  setEditableByName?: (name: string, editable: boolean) => void
}

/**
 * Re-apply ELEMENT is_visible from jsResult.states when a widget remounts
 * (tab body key change, panel v-if). PAGE/PANEL are ignored — tabs stay PAGE-only.
 */
export function applyRegisteredElementVisibilityFromStates(input: {
  states: ReplayVisibilityState[] | ElementState[] | undefined
  elementId?: string | number | null
  elementName: string
  setVisible?: (visible: boolean) => void
  setShellVisibleById?: (elementId: string | number, visible: boolean) => void
  getShellVisibleById?: (elementId: string | number) => boolean | undefined
}): boolean {
  const name = String(input.elementName ?? '').trim()
  if (name && isTilesRowScopedRegistryName(name)) return false

  const rawId = input.elementId != null && String(input.elementId).trim() !== ''
    ? Number(input.elementId)
    : NaN
  const numericId = Number.isFinite(rawId) ? rawId : 0
  const idKey = input.elementId != null ? String(input.elementId).trim() : ''
  // Hidden sibling row unmounts the widget (spacer). Client setVisible(true) already
  // wrote the shell — remount must not re-apply stale open-time is_visible:false.
  if (idKey && typeof input.getShellVisibleById === 'function') {
    const shellVisible = input.getShellVisibleById(input.elementId as string | number)
    if (typeof shellVisible === 'boolean') {
      input.setVisible?.(shellVisible)
      return true
    }
  }
  const state = findElementPageState(
    input.states as ElementState[] | undefined,
    numericId,
    name,
  )
  const visible = coerceStateBoolean(state?.is_visible)
  if (visible === undefined) return false

  input.setVisible?.(visible)
  if (idKey) {
    input.setShellVisibleById?.(input.elementId as string | number, visible)
  }
  return true
}

function resolveEditableFromElementState(state: ElementState | undefined): boolean | undefined {
  if (!state) return undefined
  const editable = coerceStateBoolean(state.is_editable)
  if (editable === undefined) return undefined
  const visible = coerceStateBoolean(state.is_visible)
  if (editable === false && visible === false) return undefined
  return editable
}

function resolveEditableFromPanelStates(
  states: ReplayVisibilityState[] | undefined,
  panelId: string | number | null | undefined,
): boolean | undefined {
  const pid = String(panelId ?? '').trim()
  if (!pid || !Array.isArray(states)) return undefined
  for (let i = states.length - 1; i >= 0; i -= 1) {
    const state = states[i]
    if (String(state?.element_level ?? '').toUpperCase() !== 'PANEL') continue
    const name = String(state?.element_name ?? '').trim()
    if (!actionIdsEqual(state?.element_id, pid) && name !== pid) continue
    const editable = coerceStateBoolean(state.is_editable)
    if (editable !== undefined) return editable
  }
  return undefined
}

/**
 * Re-apply is_editable from jsResult.states on widget remount.
 * ELEMENT wins. PANEL is_editable:false cascades to children (panel was locked).
 * PANEL is_editable:true does not — Swing JSContext skips setEditable when the panel
 * is already editable, and middle still emits the no-op (WUI-573 / form 2609).
 */
export function applyRegisteredElementEditableFromStates(input: {
  states: ReplayVisibilityState[] | ElementState[] | undefined
  elementId?: string | number | null
  elementName: string
  panelId?: string | number | null
  setEditable?: (editable: boolean) => void
}): boolean {
  const name = String(input.elementName ?? '').trim()
  if (name && isTilesRowScopedRegistryName(name)) return false

  const rawId = input.elementId != null && String(input.elementId).trim() !== ''
    ? Number(input.elementId)
    : NaN
  const numericId = Number.isFinite(rawId) ? rawId : 0
  const elementState = findElementPageState(
    input.states as ElementState[] | undefined,
    numericId,
    name,
  )
  const editable =
    resolveEditableFromElementState(elementState) ??
    resolveChildEditableFromPanelState(
      resolveEditableFromPanelStates(input.states, input.panelId),
    )
  if (editable === undefined) return false

  input.setEditable?.(editable)
  return true
}

/**
 * Re-apply stored jsResult.states after dialog slot cleanup or tab remount.
 *
 * Must not panel→children propagate (same as applyDispatchedVisibilities): propagate
 * force-shows sibling Labels, then the shell guard can block a later hide.
 *
 * Authoritative: apply hide/show even when shell is true (do not use
 * shouldApplyIncomingElementVisibility).
 */
export function replayStoredPageVisibilityStates(
  states: ReplayVisibilityState[],
  deps: ReplayStoredPageVisibilityDeps,
): void {
  if (!Array.isArray(states) || states.length === 0) return

  const byLevelOptions = { propagateChildren: false as const }

  for (const state of states) {
    const level = String(state.element_level ?? 'element').trim().toUpperCase()
    if (level === 'ACTION') continue

    const visible = coerceStateBoolean(state.is_visible)
    const editable = coerceStateBoolean(state.is_editable)
    if (visible === undefined && editable === undefined) continue

    const hasId = state.element_id != null && String(state.element_id).trim() !== ''
    const name = String(state.element_name ?? '').trim()

    if (visible !== undefined) {
      if (hasId) {
        deps.setVisibleByLevel(
          String(state.element_level ?? 'element'),
          state.element_id as string | number,
          visible,
          byLevelOptions,
        )
      } else if (name) {
        deps.setVisibleByName?.(name, visible)
      }
    }

    // persist:false задаёт вызывающий (replayPageVisibilityStatesForSlot) — states уже в слоте.
    if (editable !== undefined) {
      if (hasId) {
        deps.setEditableByLevel?.(
          String(state.element_level ?? 'element'),
          state.element_id as string | number,
          editable,
        )
      } else if (name) {
        deps.setEditableByName?.(name, editable)
      }
    }
  }
}
